import { DestroyRef, ErrorHandler, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { useStore } from 'ngx-unificator/store';
import { EMPTY, forkJoin, from, merge, of } from 'rxjs';
import { catchError, groupBy, mergeMap, switchMap, tap } from 'rxjs/operators';
import { PlatformService } from '../../services/platform.service';
import { BonusStage, BonusType } from '../../services/user/data/user-bonuses.data';
import { UserBonusesService } from '../../services/user/user-bonuses.service';
import { UserService } from '../../services/user/user.service';
import { WebSocketService } from '../../services/web-socket.service';
import { GlobalStoreName } from '../../store';
import { NotificationStoreEntity, NotificationType } from '../../store/notification.store';
import { CommonBonusType } from '../../types/bonuses.type';
import { SsWebSocketsPrivateChanel } from '../../vendor/ss-web-sockets/ss-web-sockets.types';
import { NotificationSocketType } from './notification-center.type';
import { NotificationFieldsResolverService } from './notification-fields-resolver.service';
import { NotificationCenterService } from './notification-center.service';

@Injectable({ providedIn: 'root' })
export class BonusNotificationsService {
  private _bonuses = inject(UserBonusesService);
  private _user = inject(UserService);
  private _ws = inject(WebSocketService);
  private _platform = inject(PlatformService);
  private _notificationFields = inject(NotificationFieldsResolverService);
  private _notifications = inject(NotificationCenterService);
  private _errorHandler = inject(ErrorHandler);
  private _destroyRef = inject(DestroyRef);

  private _notificationStore = useStore<NotificationStoreEntity>(GlobalStoreName.NOTIFICATION_STORE);
  private _closedNotificationIds = new Set<string>();

  public initBonusNotifications() {
    if (!this._platform.isBrowser) return;
    this._user.auth$
      .pipe(
        tap(() => this._closedNotificationIds.clear()),
        switchMap((auth) => {
          if (!auth) return EMPTY;
          return this._listenBonusNotifications();
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  public removeBonusNotification(id: string) {
    this._closedNotificationIds.add(id);
    this._notificationStore.storeSignal.update((state) => ({
      ...state,
      list: state.list.map((notification) =>
        notification.id === id ? { ...notification, isDeleted: true, deletedByBonusAction: true, deletedManually: false } : notification,
      ),
    }));
  }

  private _listenBonusNotifications() {
    const sources = [
      {
        channel: SsWebSocketsPrivateChanel.BONUSES_CHANGED,
        type: BonusType.MONEY,
        list$: this._bonuses.moneyBonuses$,
        socket$: this._ws.bonuses$,
      },
      {
        channel: SsWebSocketsPrivateChanel.FREESPINS_CHANGED,
        type: BonusType.FREE_SPINS,
        list$: this._bonuses.freeSpinBonuses$,
        socket$: this._ws.freespins$,
      },
      {
        channel: SsWebSocketsPrivateChanel.LOOTBOXES_CHANGED,
        type: BonusType.LOOTBOX,
        list$: this._bonuses.lootBoxList$,
        socket$: this._ws.lootboxes$,
      },
    ];
    return merge(
      ...sources.map(({ channel, type, list$, socket$ }) =>
        merge(
          list$.pipe(
            switchMap((list) => this._resolveBonusListNotifications(channel, list)),
            tap((notifications) => this._syncBonusNotifications(channel, notifications, true)),
          ),
          socket$.pipe(
            groupBy((notification: NotificationSocketType) => this._notificationFields.getNotificationId(notification)),
            mergeMap((notifications$) => notifications$.pipe(switchMap((notification) => this._resolveSocketBonusNotification(notification, type)))),
          ),
        ),
      ),
    );
  }

  private _resolveBonusListNotifications(channel: SsWebSocketsPrivateChanel, list: CommonBonusType[]) {
    const active = list
      .map((bonus) => ({ notify_channel: channel, id: bonus.id, title: bonus.title, stage: bonus.stage, strategy: bonus.strategy, bonus }))
      .filter((notification) => this._isBonusNotificationAvailable(notification));
    return active.length ? forkJoin(active.map((notification) => this._resolveNotification(notification))) : of([]);
  }

  private _resolveSocketBonusNotification(notification: NotificationSocketType, type: BonusType) {
    const id = this._notificationFields.getNotificationId(notification);
    if (!this._isBonusNotificationAvailable(notification)) {
      this._closedNotificationIds.add(id);
      this._notificationStore.storeSignal.update((state) => ({ ...state, list: state.list.filter((item) => item.id !== id) }));
      return EMPTY;
    }
    const bonus = this._prepareSocketBonus(notification, type);
    return this._resolveNotification({ ...notification, bonus }).pipe(tap((item) => this._syncBonusNotifications(notification.notify_channel, [item])));
  }

  private _resolveNotification(notification: NotificationSocketType) {
    return from(this._notificationFields.resolveNotificationFields(notification)).pipe(
      catchError((error) => {
        this._errorHandler.handleError(error);
        return of(null);
      }),
    );
  }

  private _prepareSocketBonus(notification: NotificationSocketType, type: BonusType) {
    const cached = this._bonuses.getPlayerBonus(type, notification.id);
    const source = {
      ...cached,
      ...notification,
      title: notification.title || cached?.title || '',
      games_info: notification.games_info?.length ? notification.games_info : cached?.games_info,
      activatable_until: notification.activatable_until ? new Date(notification.activatable_until) : cached?.activatable_until,
      valid_until: notification.valid_until ? new Date(notification.valid_until) : cached?.valid_until,
    };
    if (type === BonusType.LOOTBOX) {
      source.lootboxTitle = notification.title || cached?.lootboxTitle;
    }
    return this._bonuses.prepareBonus(source, type);
  }

  private _syncBonusNotifications(channel: string, notifications: NotificationType[], replace = false) {
    const active = notifications.filter((notification) => notification && !this._closedNotificationIds.has(notification.id));
    if (replace) {
      this._notifications.syncNotifications(channel, active.reverse());
    } else {
      active.forEach((notification) => this._notifications.addNotification(notification, { ignoreExpired: true }));
    }
  }

  private _isBonusNotificationAvailable(notification: NotificationSocketType) {
    return (
      (notification.stage === BonusStage.ISSUED ||
        (notification.notify_channel === SsWebSocketsPrivateChanel.FREESPINS_CHANGED && notification.stage === BonusStage.ACTIVATED) ||
        (notification.notify_channel === SsWebSocketsPrivateChanel.BONUSES_CHANGED &&
          [BonusStage.ACTIVATED, BonusStage.HANDLE_BETS].some((stage) => stage === notification.stage))) &&
      notification.strategy !== 'freespins_result'
    );
  }
}
